import React, { useEffect, useMemo, useRef, useState } from 'react';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import { RC_TREE_MOTION } from 'utils/rcTreeMotion';
import {
  assetActions,
  AssetAttributesInfo,
  AssetElementsInfo,
  AssetInfo,
} from 'actions/asset';
import type { AssetHubModalDetails } from '../assetHubModalTypes';
import { getUnknownErrorMessage } from '../assetHubModalTypes';
import PerformanceIndicator from './PerformanceIndicator';
import type { InspectionSelection } from '../assetHubInspectionSelection';
import { inspectionSelectionKey } from '../assetHubInspectionSelection';
import {
  fetchInspectionItems,
  getInspectionRecordId,
  getInspectionTypeName,
  isAssetInspectionRecord,
  isRecordLike,
  resolveInspectionSummaryDates,
  resolveSelectedInspectionDetails,
  type InspectionItem,
} from './assetHubInspections';
import type { AssetInspectionRecord } from 'actions/asset';

export interface AssetHubTreeNode {
  readonly key: string;
  readonly title: string;
  readonly parent?: string;
  children?: AssetHubTreeNode[];
  readonly [key: string]: unknown;
}

type AssetDetailsForHub = AssetInfo & {
  readonly attributes?: boolean;
  readonly elements?: boolean;
};

type LegendTreeNode = AssetHubTreeNode & {
  readonly assetId?: unknown;
  readonly spanNumber?: unknown;
  readonly id?: unknown;
};

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function parseLegendSelectionNode(raw: unknown): LegendTreeNode | null {
  if (!isRecordLike(raw)) return null;
  const keyVal = raw['key'];
  if (typeof keyVal !== 'string' && typeof keyVal !== 'number') return null;
  const titleRaw = raw['title'];
  const title =
    typeof titleRaw === 'string'
      ? titleRaw
      : typeof titleRaw === 'number'
        ? String(titleRaw)
        : '';
  const parent = readOptionalString(raw['parent']);
  const childrenRaw = raw['children'];
  const out: Record<string, unknown> = {
    key: String(keyVal),
    title,
    assetId: raw['assetId'],
    spanNumber: raw['spanNumber'],
    id: raw['id'],
  };
  if (parent !== undefined) {
    out.parent = parent;
  }
  if (Array.isArray(childrenRaw)) {
    out.children = childrenRaw;
  }
  for (const k of Object.keys(raw)) {
    if (
      k === 'key' ||
      k === 'title' ||
      k === 'parent' ||
      k === 'children' ||
      k === 'assetId' ||
      k === 'spanNumber' ||
      k === 'id'
    ) {
      continue;
    }
    out[k] = raw[k];
  }
  return out as LegendTreeNode;
}

const DEFAULT_TREE_DATA: AssetHubTreeNode[] = [
  {
    key: 'parent-asset',
    title: 'Bridge Asset( No. 620B)',
    children: [],
  },
];

type AssetHubLegendProps = {
  data: { id?: number; category?: string };
  modalDetails: AssetHubModalDetails | undefined;
  setModalDetails: React.Dispatch<
    React.SetStateAction<AssetHubModalDetails | undefined>
  >;
  infoTrigger: boolean;
  setInfoTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  setSpanInfoError: React.Dispatch<React.SetStateAction<boolean>>;
  assetHubRefreshToken: number;
  setSelectedInspection: React.Dispatch<
    React.SetStateAction<InspectionSelection | null>
  >;
  selectedInspection: InspectionSelection | null;
  readonly inspectionRecords: readonly AssetInspectionRecord[];
  readonly inspectionRecordsLoading: boolean;
};

const AssetHubLegend: React.FC<AssetHubLegendProps> = ({
  data,
  modalDetails,
  setModalDetails,
  infoTrigger,
  setInfoTrigger,
  setSpanInfoError,
  assetHubRefreshToken,
  selectedInspection,
  setSelectedInspection,
  inspectionRecords,
  inspectionRecordsLoading,
}) => {
  const refreshData = assetHubRefreshToken > 0;
  const [attributes, setAttributes] = useState<AssetAttributesInfo[]>([]);
  const [inspectionDates, setInspectionDates] = useState<InspectionItem[]>([]);
  const [elements, setElements] = useState<AssetElementsInfo[]>([]);
  const [showAttributes, setShowAttributes] = useState<boolean>(false);
  const [showElements, setShowElements] = useState<boolean>(false);
  const [treeData, setTreeData] =
    useState<AssetHubTreeNode[]>(DEFAULT_TREE_DATA);
  const [assetId, setAssetId] = useState<number>(0);
  const selectedInspectionRef = useRef(selectedInspection);
  selectedInspectionRef.current = selectedInspection;
  const currentAssetId = data?.id ?? assetId;
  const inspectionRecordsKey = `${currentAssetId}:${assetHubRefreshToken}:${inspectionRecords
    .map((record) => String(record.id))
    .join(',')}`;
  const NODE_ORDER: Record<string, number> = {
    '0-0-0': 0, // Attributes
    '0-0-1': 1, // Inspections
    '0-0-2': 2, // Spans
  };

  const sortRootChildren = (children: AssetHubTreeNode[]): AssetHubTreeNode[] =>
    [...children].sort(
      (a, b) =>
        (NODE_ORDER[a.key] ?? Number.MAX_SAFE_INTEGER) -
        (NODE_ORDER[b.key] ?? Number.MAX_SAFE_INTEGER),
    );

  const upsertRootNodeByKey = (
    children: AssetHubTreeNode[],
    node: AssetHubTreeNode,
  ): AssetHubTreeNode[] => {
    const nextChildren = [...children];
    const existingIndex = nextChildren.findIndex((child) => child.key === node.key);
    if (existingIndex >= 0) {
      nextChildren[existingIndex] = node;
    } else {
      nextChildren.push(node);
    }
    return sortRootChildren(nextChildren);
  };

  const removeRootNodeByKey = (
    children: AssetHubTreeNode[],
    key: string,
  ): AssetHubTreeNode[] => children.filter((child) => child.key !== key);

  const displayedTreeData = useMemo(() => {
    if (inspectionDates.length > 0) return treeData;
    const localTree: AssetHubTreeNode[] = treeData.map((node) => ({
      ...node,
      children: node.children
        ? node.children.map((c) => ({ ...c }))
        : undefined,
    }));
    if (localTree[0]?.children) {
      localTree[0] = {
        ...localTree[0],
        children: localTree[0].children.filter((child) => child.key !== '0-0-1'),
      };
    }
    return localTree;
  }, [treeData, inspectionDates]);

  const buildInspectionNode = (
    inspectionResult: InspectionItem[],
  ): AssetHubTreeNode | null => {
    if (!inspectionResult || inspectionResult.length === 0) return null;

    const inspectionGroups: Record<string, InspectionItem[]> = {};
    inspectionResult.forEach((item) => {
      const inspectionType = getInspectionTypeName(item);
      if (!inspectionType) return;
      if (!inspectionGroups[inspectionType]) {
        inspectionGroups[inspectionType] = [];
      }
      inspectionGroups[inspectionType].push(item);
    });

    if (Object.keys(inspectionGroups).length === 0) return null;

    const parentTitle =
      Object.keys(inspectionGroups).length > 1 ? 'Inspections' : 'Inspection';

    return {
      key: '0-0-1',
      title: parentTitle,
      children: Object.keys(inspectionGroups).map((type) => {
        const records = inspectionGroups[type]
          .filter(isAssetInspectionRecord)
          .sort((a, b) => b.inspectionDate.localeCompare(a.inspectionDate));

        return {
          key: type.toLowerCase().replace(/\s/g, '-'),
          title: type,
          parent: parentTitle,
          children: records.map((record) => ({
            key: `insp-${record.id}`,
            title: record.inspectionDate,
            parent: type,
            inspectionType: type,
            recordId: record.id,
            children: [],
          })),
        };
      }),
    };
  };

  useEffect(() => {
    if (!showAttributes || data?.category !== 'asset' || !currentAssetId) {
      return;
    }

    let cancelled = false;

    const fetchAssetAttributes = async () => {
      try {
        const result = await assetActions().getAssetAttributes(
          currentAssetId,
          refreshData,
        );
        if (cancelled) return;
        setAttributes(result);
      } catch (error: unknown) {
        if (cancelled) return;
        console.error(
          'Failed to fetch asset attributes:',
          getUnknownErrorMessage(error),
        );
      }
    };

    fetchAssetAttributes();

    return () => {
      cancelled = true;
    };
  }, [showAttributes, assetHubRefreshToken, currentAssetId, refreshData, data?.category]);

  useEffect(() => {
    if (!showAttributes || attributes.length === 0) {
      return;
    }

    const attributesNode: AssetHubTreeNode = {
      key: '0-0-0',
      title: 'Attributes',
      children: attributes.map((item, index) => ({
        key: `attr-${item.name}-${index}`,
        title: item.name,
        [item.name]: item.value,
        parent: 'Attributes',
      })),
    };

    setTreeData((prevTreeData) => {
      const localTree = [...prevTreeData];
      if (!localTree[0]) return localTree;
      localTree[0] = {
        ...localTree[0],
        children: upsertRootNodeByKey(
          localTree[0].children ?? [],
          attributesNode,
        ),
      };
      return localTree;
    });
  }, [showAttributes, attributes]);

  useEffect(() => {
    if (
      modalDetails == null ||
      !currentAssetId ||
      typeof modalDetails !== 'object' ||
      Array.isArray(modalDetails)
    ) {
      return;
    }

    const details = modalDetails as AssetDetailsForHub;
    const isAssetDetails =
      'assetTypeCode' in details ||
      'number' in details ||
      'attributes' in details ||
      'elements' in details;

    if (!isAssetDetails) {
      return;
    }

    // Keep attribute/element tree flags from the asset payload only (not inspection rows).
    if ('attributes' in details) {
      setShowAttributes(Boolean(details.attributes));
    }
    if ('elements' in details) {
      setShowElements(Boolean(details.elements));
    }
    if (details.number != null) {
      setTreeData((prevTreeData) => {
        const localTree = [...prevTreeData];
        if (localTree[0]) {
          localTree[0] = {
            ...localTree[0],
            title: `Asset No. ${details.number}`,
          };
        }
        return localTree;
      });
    }
  }, [modalDetails, currentAssetId]);

  useEffect(() => {
    if (!showElements || data?.category !== 'asset') {
      setTreeData((prevTreeData) => {
        const localTree = [...prevTreeData];
        if (!localTree[0]) return localTree;
        localTree[0] = {
          ...localTree[0],
          children: removeRootNodeByKey(localTree[0].children ?? [], '0-0-2'),
        };
        return localTree;
      });
      return;
    }
    if (!currentAssetId) return;

    const fetchAssetElements = async () => {
      try {
        const result = await assetActions().getAssetElements(
          currentAssetId,
          refreshData,
        );
        const elementResult = Array.isArray(result) ? result : [];
        setElements(elementResult);

        const groupedBySpan = elementResult.reduce<
          Record<number, typeof elementResult>
        >((acc, item) => {
          const span = item.spanNumber ?? 0;
          if (!acc[span]) acc[span] = [];
          acc[span].push(item);
          return acc;
        }, {});

        const spanChildren: AssetHubTreeNode[] = Object.keys(groupedBySpan)
          .map((span) => Number(span))
          .sort((a, b) => a - b)
          .map((spanNumber) => ({
            key: `span-${spanNumber}`,
            title: `Span ${spanNumber}`,
            parent: 'Spans',
            children: (groupedBySpan[spanNumber] ?? []).map((item, index) => ({
              key: `el-${spanNumber}-${item.id ?? index}`,
              title: item.name || `Element ${index + 1}`,
              id: item.id,
              spanNumber,
              assetId: currentAssetId,
              parent: `Span ${spanNumber}`,
            })),
          }));

        const spansNode: AssetHubTreeNode = {
          key: '0-0-2',
          title: 'Spans',
          children: spanChildren,
        };

        setTreeData((prevTreeData) => {
          const localTree = [...prevTreeData];
          if (!localTree[0]) return localTree;
          localTree[0] = {
            ...localTree[0],
            children: upsertRootNodeByKey(localTree[0].children ?? [], spansNode),
          };
          return localTree;
        });
      } catch (error: unknown) {
        console.error(
          'Failed to fetch asset elements:',
          getUnknownErrorMessage(error),
        );
      }
    };

    fetchAssetElements();
  }, [showElements, currentAssetId, assetHubRefreshToken, refreshData]);

  useEffect(() => {
    if (!currentAssetId) return;
    setInspectionDates([]);
    setTreeData((prevTreeData) => {
      const localTree = [...prevTreeData];
      if (localTree[0]?.children) {
        localTree[0] = {
          ...localTree[0],
          children: localTree[0].children.filter((child) => child.key !== '0-0-1'),
        };
      }
      return localTree;
    });
  }, [currentAssetId]);

  useEffect(() => {
    if (!currentAssetId || inspectionRecordsLoading) return;

    let cancelled = false;

    const applyInspectionSource = async () => {
      try {
        // Shared React Query owns /inspections/records. Only fall back to
        // /inspections/dates when that query settled empty.
        let inspectionSource: InspectionItem[] = [...inspectionRecords];
        if (inspectionSource.length === 0) {
          inspectionSource = await fetchInspectionItems(
            currentAssetId,
            refreshData,
            inspectionRecords,
          );
        }

        if (cancelled) return;

        setInspectionDates(inspectionSource);
        if (inspectionSource.length === 0) {
          setTreeData((prevTreeData) => {
            const localTree = [...prevTreeData];
            if (localTree[0]?.children) {
              localTree[0] = {
                ...localTree[0],
                children: localTree[0].children.filter(
                  (child) => child.key !== '0-0-1',
                ),
              };
            }
            return localTree;
          });
          return;
        }

        const inspectionNode = buildInspectionNode(inspectionSource);
        if (!inspectionNode) return;

        setTreeData((prevTreeData) => {
          const localTree = [...prevTreeData];
          if (!localTree[0]) return localTree;
          localTree[0] = {
            ...localTree[0],
            children: upsertRootNodeByKey(
              localTree[0].children ?? [],
              inspectionNode,
            ),
          };
          return localTree;
        });

        const selection = selectedInspectionRef.current;
        if (selection) {
          const refreshedDetails = resolveSelectedInspectionDetails(
            inspectionSource,
            selection,
          );
          if (refreshedDetails) {
            setModalDetails(refreshedDetails);
          }
        }
      } catch (error: unknown) {
        if (cancelled) return;
        console.error(
          'Failed to fetch survey data:',
          getUnknownErrorMessage(error),
        );
        setTreeData((prevTreeData) => {
          const localTree = [...prevTreeData];
          if (localTree[0]?.children) {
            localTree[0] = {
              ...localTree[0],
              children: localTree[0].children.filter(
                (child) => child.key !== '0-0-1',
              ),
            };
          }
          return localTree;
        });
      }
    };

    applyInspectionSource();

    return () => {
      cancelled = true;
    };
  }, [
    currentAssetId,
    assetHubRefreshToken,
    refreshData,
    setModalDetails,
    inspectionRecordsKey,
    inspectionRecordsLoading,
  ]);

  useEffect(() => {
    if (data?.id) {
      setAssetId(data.id);
      return;
    }
    if (
      modalDetails != null &&
      typeof modalDetails === 'object' &&
      !Array.isArray(modalDetails) &&
      'id' in modalDetails &&
      typeof (modalDetails as { id?: unknown }).id === 'number'
    ) {
      setAssetId((modalDetails as { id: number }).id);
    }
  }, [data?.id, modalDetails]);

  type TreeOnSelect = NonNullable<React.ComponentProps<typeof Tree>['onSelect']>;

  const selectInspectionType = (inspectionType: string, recordId?: number | string | null) => {
    const inspectionSelection: InspectionSelection = {
      inspectionType,
      recordId: recordId ?? null,
    };
    setSelectedInspection(inspectionSelection);

    const resolveFromItems = (inspectionItems: readonly InspectionItem[]) => {
      if (recordId != null) {
        const matchingRecord = inspectionItems.find(
          (item) =>
            isAssetInspectionRecord(item) &&
            String(item.id) === String(recordId),
        );
        if (matchingRecord) {
          setSelectedInspection({
            inspectionType,
            recordId: getInspectionRecordId(matchingRecord),
          });
          setModalDetails(matchingRecord);
          return;
        }
      }

      const inspectionDetail = resolveSelectedInspectionDetails(
        inspectionItems,
        inspectionSelection,
      );
      if (inspectionDetail) {
        setSelectedInspection({
          inspectionType,
          recordId: getInspectionRecordId(inspectionDetail),
        });
        setModalDetails(inspectionDetail);
      } else {
        setModalDetails({
          message: 'No details available for selected inspection.',
        });
      }
    };

    if (inspectionDates.length > 0) {
      resolveFromItems(inspectionDates);
      return;
    }

    fetchInspectionItems(currentAssetId, refreshData)
      .then(resolveFromItems)
      .catch(() => {
        setModalDetails({
          message: 'No details available for selected inspection.',
        });
      });
  };

  const handleNodeClick: TreeOnSelect = (selectedKeys, info) => {
    setSpanInfoError(false);
    const raw = info.selectedNodes?.[0];
    if (!raw) return;
    const selectedNode = parseLegendSelectionNode(raw);
    if (!selectedNode) return;

    const nodeTitle =
      typeof selectedNode.title === 'string' ? selectedNode.title : '';
    const nodeParent =
      typeof selectedNode.parent === 'string' ? selectedNode.parent : '';
    const hasChildren =
      Array.isArray(selectedNode.children) && selectedNode.children.length > 0;

    if (!hasChildren && nodeParent) {
      if (nodeParent === 'Attributes') {
        const attributeName = nodeTitle;
        if (attributeName) {
          const rawValue = selectedNode[attributeName];
          setModalDetails({
            [attributeName]:
              rawValue !== undefined && rawValue !== null
                ? String(rawValue)
                : '',
          });
        }
      } else if (nodeParent === 'Inspections' || nodeParent === 'Inspection') {
        selectInspectionType(nodeTitle);
      } else if (selectedNode.recordId != null) {
        const inspectionType =
          typeof selectedNode.inspectionType === 'string'
            ? selectedNode.inspectionType
            : nodeParent;
        selectInspectionType(inspectionType, selectedNode.recordId as number | string);
      } else {
        const spanNumber = Number(selectedNode.spanNumber);
        const elementId = Number(selectedNode.id);
        const assetForElement = Number(selectedNode.assetId ?? currentAssetId);
        if (
          Number.isFinite(spanNumber) &&
          Number.isFinite(elementId) &&
          Number.isFinite(assetForElement)
        ) {
          assetActions()
            .getElementAttributes(
              assetForElement,
              spanNumber,
              elementId,
              refreshData,
            )
            .then((result) => {
              const combinedObject: Record<string, string> = {};
              result.forEach((item) => {
                if (item.name && item.value) {
                  combinedObject[item.name] = item.value;
                }
              });
              setModalDetails(combinedObject);
            })
            .catch(() => {
              setSpanInfoError(true);
              setModalDetails(null);
            });
        }
      }
    } else if (nodeTitle === 'Attributes') {
      assetActions()
        .getAssetAttributes(currentAssetId, refreshData)
        .then((result) => {
          setModalDetails(result);
        })
        .catch(() => {
          /* ignore */
        });
    } else if (nodeParent === 'Inspections' || nodeParent === 'Inspection') {
      selectInspectionType(nodeTitle);
    } else if (nodeTitle === 'Inspections' || nodeTitle === 'Inspection') {
      assetActions()
        .getAssetInspectionDetails(currentAssetId, refreshData)
        .then((result) => {
          const rows = (Array.isArray(result) ? result : []).filter(Boolean) as Array<{
            lastInspectionDate?: string;
            nextInspectionDate?: string;
          }>;
          setModalDetails(
            resolveInspectionSummaryDates(rows, inspectionRecords),
          );
        })
        .catch((error: unknown) => {
          console.error('Failed to fetch inspection details:', error);
        });
    } else {
      setInfoTrigger(!infoTrigger);
    }
  };

  return (
    <div className="xa_asset_hub--legend_container">
      {currentAssetId > 0 && (
        <PerformanceIndicator
          assetId={currentAssetId}
          refreshToken={assetHubRefreshToken}
        />
      )}
      <Tree
        showLine
        selectable
        expandAction="click"
        motion={RC_TREE_MOTION}
        defaultExpandParent={true}
        autoExpandParent={true}
        treeData={displayedTreeData}
        showIcon={false}
        onSelect={handleNodeClick}
        defaultExpandedKeys={['parent-asset']}
        selectedKeys={
          selectedInspection?.recordId != null
            ? [`insp-${selectedInspection.recordId}`]
            : selectedInspection
              ? [inspectionSelectionKey(selectedInspection.inspectionType)]
              : []
        }
      />
    </div>
  );
};

export default AssetHubLegend;
